import React, { useRef, useEffect } from 'react';
import { ChatBubble } from './ChatBubble';
import { QuickActionsButtons } from './QuickActionsButtons';
import { useChatMessages } from '../../hooks/useChatMessages';
import { useStreamingResponse } from '../../hooks/useStreamingResponse';
import { TypingIndicator } from './TypingIndicator';

export function ChatMessages() {
  const { messages } = useChatMessages();
  const { isStreaming } = useStreamingResponse();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const prevMessagesLengthRef = useRef(0);
  const isFirstBotResponseRef = useRef(false);

  // Fonction de scroll
  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    if (scrollContainerRef.current) {
      const scrollContainer = scrollContainerRef.current;
      const scrollHeight = scrollContainer.scrollHeight;
      const height = scrollContainer.clientHeight;
      const maxScrollTop = scrollHeight - height;

      scrollContainer.scrollTo({
        top: maxScrollTop + 100, // +100 pour assurer qu'on va bien jusqu'en bas
        behavior
      });
    }
  };

  // Scroll quand de nouveaux messages arrivent
  useEffect(() => {
    // Détecter si un nouveau message bot vient d'être ajouté
    const isNewBotMessage = 
      messages.length > prevMessagesLengthRef.current && 
      messages[messages.length - 1]?.type === 'bot';

    // Si c'est le premier message du bot dans cette séquence
    if (isNewBotMessage && !isFirstBotResponseRef.current) {
      isFirstBotResponseRef.current = true;
      
      // Scroll au début de la réponse du bot
      if (lastMessageRef.current && scrollContainerRef.current) {
        // Use scrollIntoView with preventScroll option to prevent page scrolling
        const scrollableParent = scrollContainerRef.current;
        
        // Calculate the position to scroll to
        const parentRect = scrollableParent.getBoundingClientRect();
        const targetRect = lastMessageRef.current.getBoundingClientRect();
        const relativeTop = targetRect.top - parentRect.top;
        
        // Scroll the container, not the page
        scrollableParent.scrollTop = relativeTop + scrollableParent.scrollTop - 20; // 20px offset for better visibility
      }
    }
    
    // Si le nombre de messages a changé
    if (messages.length !== prevMessagesLengthRef.current) {
      // Scroll instantané au premier message
      if (messages.length === 1) {
        scrollToBottom('instant');
      } else {
        // Pour les messages suivants, scroll smooth
        const timer = setTimeout(() => {
          scrollToBottom('smooth');
          
          // Réinitialiser le flag après avoir scrollé à la fin de la réponse
          if (isFirstBotResponseRef.current && messages[messages.length - 1]?.type === 'bot') {
            isFirstBotResponseRef.current = false;
          }
        }, 100);
        
        return () => clearTimeout(timer);
      }
    }
    
    // Mettre à jour la référence du nombre de messages
    prevMessagesLengthRef.current = messages.length;
  }, [messages]);

  return (
    <div 
      ref={scrollContainerRef}
      className="h-full overflow-y-auto overscroll-contain scroll-smooth hide-scrollbar"
      style={{
        scrollbarWidth: 'none', /* Cacher la barre de défilement sur Firefox */
        msOverflowStyle: 'none', /* IE et Edge */
        WebkitOverflowScrolling: 'touch'
      }}
    >
      <div className="flex flex-col px-2 sm:px-3 md:px-4 py-3 space-y-1">
        {messages.map((message, index) => (
          <div 
            key={message.id}
            ref={index === messages.length - 1 ? lastMessageRef : null}
          >
            <ChatBubble message={message} />
          </div>
        ))}
        
        {/* Indicateur de frappe pendant le streaming */}
        {isStreaming && <TypingIndicator />}
        
        {messages[messages.length - 1]?.type === 'bot' && <QuickActionsButtons />}
      </div>
    </div>
  );
}